import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useMostVisitedPages } from '../../hooks/usePageTracking';
import { formatDateTime } from '../../utils/formatUtils';
import { HeadTableCell, RowTableCell, GeneralTableBox, CustomButton } from '../../common';

const PageVisitStatsComponent = () => {
  const [open, setOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const { mostVisitedPages, hasSufficientData, totalPagesVisited, clearAllVisits } =
    useMostVisitedPages();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClearStats = () => {
    clearAllVisits();
    setConfirmClearOpen(false);
  };

  const formatLastVisit = (timestamp) =>
    formatDateTime(timestamp, {
      options: { dateStyle: 'short', timeStyle: 'short' },
      fallback: 'Nunca',
    });

  const getPopularityLevel = (visitCount) => {
    if (visitCount >= 10) return { label: 'Muy Popular', color: 'success' };
    if (visitCount >= 5) return { label: 'Popular', color: 'warning' };
    if (visitCount >= 2) return { label: 'Ocasional', color: 'info' };
    return { label: 'Poco Visitada', color: 'default' };
  };

  return (
    <>
      <Tooltip title="Ver estadísticas de páginas visitadas">
        <CustomButton
          variant="outlined"
          startIcon={<AnalyticsIcon />}
          onClick={handleOpen}
          sx={{
            color: 'primary.main',
            borderColor: 'primary.main',
            width: { xs: '100%', md: 'auto' },
            '&:hover': {
              backgroundColor: 'primary.main',
              color: 'white',
            },
          }}
        >
          Estadísticas de Uso
        </CustomButton>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <AnalyticsIcon />
            <Typography variant="h6">Estadísticas de Páginas Visitadas</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <CardContent>
                  <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {totalPagesVisited}
                  </Typography>
                  <Typography variant="body2">Páginas Únicas Visitadas</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
                <CardContent>
                  <VisibilityIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {mostVisitedPages.reduce((total, page) => total + page.count, 0)}
                  </Typography>
                  <Typography variant="body2">Total de Visitas</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Card sx={{ textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                <CardContent>
                  <ScheduleIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {hasSufficientData ? 'SÍ' : 'NO'}
                  </Typography>
                  <Typography variant="body2">Datos Suficientes</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>¿Cómo funciona?</strong> El sistema registra automáticamente cada página que
              visitas. Cuando tienes suficientes datos (al menos 3 páginas con 2+ visitas), las
              cards del inicio se ordenan por popularidad. Si no hay suficientes datos, se muestran
              aleatoriamente.
            </Typography>
          </Box>

          {mostVisitedPages.length > 0 ? (
            <GeneralTableBox>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeadTableCell>
                      <strong>Página</strong>
                    </HeadTableCell>
                    <HeadTableCell>
                      <strong>Ruta</strong>
                    </HeadTableCell>
                    <HeadTableCell align="center">
                      <strong>Visitas</strong>
                    </HeadTableCell>
                    <HeadTableCell align="center">
                      <strong>Popularidad</strong>
                    </HeadTableCell>
                    <HeadTableCell>
                      <strong>Última Visita</strong>
                    </HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mostVisitedPages.map((page, index) => {
                    const popularity = getPopularityLevel(page.count);
                    return (
                      <TableRow key={page.path} hover>
                        <RowTableCell>{page.title}</RowTableCell>
                        <RowTableCell>{page.path}</RowTableCell>
                        <RowTableCell>
                          <Chip label={page.count} color="primary" size="small" />
                        </RowTableCell>
                        <RowTableCell>
                          <Chip label={popularity.label} color={popularity.color} size="small" />
                        </RowTableCell>
                        <RowTableCell>{formatLastVisit(page.lastVisited)}</RowTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </GeneralTableBox>
          ) : (
            <Box textAlign="center" py={4}>
              <AnalyticsIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No hay estadísticas disponibles
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Navega por la aplicación para empezar a generar estadísticas
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Box display="flex" gap={1} width="100%" justifyContent="space-between">
            <CustomButton
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmClearOpen(true)}
              disabled={mostVisitedPages.length === 0}
            >
              Limpiar Estadísticas
            </CustomButton>

            <CustomButton variant="contained" startIcon={<RefreshIcon />} onClick={handleClose}>
              Cerrar
            </CustomButton>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación para limpiar */}
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>¿Confirmar eliminación?</DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción eliminará todas las estadísticas de páginas visitadas. Esta acción no se
            puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton onClick={() => setConfirmClearOpen(false)}>Cancelar</CustomButton>
          <CustomButton onClick={handleClearStats} color="error" variant="contained">
            Eliminar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PageVisitStatsComponent;
